import {
  useSyncExternalStore,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { Inspector } from '../Inspector';
import { createMachine } from '../createMachine';
import {
  MachineDefinition,
  CreateMachineOptions,
  MachineState,
  RestartOptions,
} from '../types';

import { useIsReady } from './useIsReady';

export type StateMachineReactContext<
  TContext = any,
  TEvent extends { type: string } = any,
> = {
  state: {
    value: MachineState;
    context: TContext;
    hasTag: (tag: string) => boolean;
    isRunning: () => boolean;
    matches: (name: string) => boolean;
    getInspector: () => Inspector<TEvent>;
  };
  send: (event: TEvent['type'] | TEvent) => void;
  restart: (options?: RestartOptions<TContext>) => void;
};

export function useCreateMachine<
  TContext = any,
  TEvent extends { type: string; data?: any } = any,
>(
  machineDefinition: MachineDefinition<TContext, TEvent>,
  options: CreateMachineOptions<TContext, TEvent> & { debug?: boolean } = {}
) {
  const isReady = useIsReady(options);

  const isEnabled =
    typeof options.isEnabled === 'function'
      ? options.isEnabled()
      : options.isEnabled ?? true;

  const [machine, setMachine] = useState(() => {
    return createMachine<TContext, TEvent>(machineDefinition, {
      ...options,
      waitToStart: true,
      debug: options.debug,
    });
  });
  const restartedMachine = useRef<typeof machine | null>(null);
  const wasDisabled = useRef(false);

  const snapshot = useSyncExternalStore(
    useCallback((listener) => machine.subscribe(listener), [machine]),
    () => machine.getSnapshot(),
    () => machine.getSnapshot()
  );

  const state = useMemo(
    () => ({
      value: snapshot.value,
      context: snapshot.context,
      hasTag: (tag: string) => machine.hasTagForState(snapshot.value, tag),
      isRunning: () => machine.isRunning(),
      matches: (name: string) => snapshot.value === name,
      getInspector: () => machine.getInspector(),
    }),
    [snapshot, machine]
  );

  useEffect(() => {
    // The initial machine is created during render so useSyncExternalStore has
    // a snapshot for SSR and the first render. It is replaced below once the
    // client is ready, so dispose of it before creating the owned instance.
    machine.stop();

    const newMachine = createMachine<TContext>(machineDefinition, {
      ...options,
      waitToStart: !isReady,
      debug: options.debug,
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMachine(newMachine);

    return () => {
      newMachine.stop();
    };

    /**
     * We specifically only want to run this effect once, after the client initializes.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      if (machine.isRunning()) {
        machine.stop();
        wasDisabled.current = true;
      }
      return;
    }

    if (machine.isRunning() || !isReady) {
      return;
    }

    if (!wasDisabled.current && restartedMachine.current !== machine) {
      machine.mergeContext(options.initialContext);
    }
    wasDisabled.current = false;
    machine.start();

    /**
     * We only want to listen to isReady/isEnabled and don't care if the machine changes.
     * If both gates are open and the machine isn't already running we need to update it
     * with the latest context and then start it. If isEnabled becomes false we stop it.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, isEnabled]);

  const send = useCallback(
    (event: TEvent['type'] | TEvent) => {
      machine.send(event);
    },
    [machine]
  );

  const restart = useCallback(
    (restartOptions?: RestartOptions<TContext>) => {
      restartedMachine.current = machine;
      machine.restart(restartOptions);
    },
    [machine]
  );

  return { state, send, restart };
}
